function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function getFocusableElements(container) {
  const elements = Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );

  // Filter out elements that are not visible.
  // Copied from jQuery https://github.com/jquery/jquery/blob/2d4f53416e5f74fa98e0c1d66b6f3c285a12f0ce/src/css/hiddenVisibleSelectors.js
  return elements.filter(
    (element) =>
      !!(
        element.offsetWidth ||
        element.offsetHeight ||
        element.getClientRects().length
      ),
  );
}

function hideElement(element) {
  element.style.display = "none";
  element.setAttribute("aria-hidden", true);
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    ) {
      return;
    }

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

function initDisclosureWidgets(summaries) {
  summaries.forEach((summary) => {
    summary.setAttribute("role", "button");
    summary.setAttribute(
      "aria-expanded",
      summary.parentNode.hasAttribute("open"),
    );

    if (summary.nextElementSibling.getAttribute("id")) {
      summary.setAttribute("aria-controls", summary.nextElementSibling.id);
    }

    summary.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open"),
      );
    });

    summary.parentElement.addEventListener("keyup", onKeyUpEscape);
  });
}

function renderIconHTML(escapedString, width, height) {
  const parser = new DOMParser();
  const dom = parser.parseFromString(escapedString, "text/html");
  width && dom.querySelector("svg").setAttribute("width", width);
  height && dom.querySelector("svg").setAttribute("height", height);
  return dom.body.innerHTML;
}

if (!customElements.get("custom-modal")) {
  customElements.define(
    "custom-modal",
    class CustomModal extends HTMLElement {
      constructor() {
        super();
        this.attachEventHandlers();
      }

      connectedCallback() {
        this.render();
      }

      disconnectedCallback() {
        this.removeEventHandlers();
      }

      attachEventHandlers() {
        // Find all buttons that target this modal
        const modalId = this.getAttribute("id");
        this.eventListeners = [];
        document
          .querySelectorAll(`[data-target-modal="${modalId}"]`)
          .forEach((button) => {
            const listener = (e) => {
              const direction =
                button.getAttribute("data-direction") || "bottom";
              setTimeout(() => {
                document
                  .querySelector(
                    `custom-modal#${button.dataset.targetModal}, geolocation-modal#${button.dataset.targetModal}`,
                  )
                  .openModal(direction);
              }, 1);
            };
            button.addEventListener("click", listener);
            this.eventListeners.push({ element: button, listener });
          });
      }

      removeEventHandlers() {
        this.eventListeners.forEach(({ element, listener }) => {
          element.removeEventListener("click", listener);
        });
        this.eventListeners = [];
      }

      render() {
        this.classList =
          "fixed inset-0 w-full h-full flex items-end lg:items-center justify-center z-[500] px-5 md:px-[30px] hidden";
        this.style.visibility = "visible";
        const slot = this.innerHTML;
        this.innerHTML = `
          <div id="overlay" class="fixed inset-0 z-40 bg-background opacity-0 hidden transition-opacity"></div>
          <div id="modal" class="relative shadow-lg flex flex-col w-auto bg-background overflow-hidden outline-none focus:outline-none rounded-[20px] lg:my-0 my-4 z-[100]">
              <button id="closeBtn" type="button" class="absolute hover:bg-muted-foreground transition-colors right-0 top-0 p-4">${renderIconHTML(
                window.icons.closeNew,
                16,
                16,
              )}</button>
              <div>${slot}</div>
          </div>
        `;
        this.modal = this.querySelector("#modal");
        this.overlay = this.querySelector("#overlay");
        this.closeBtn = this.querySelector("#closeBtn");
      }

      openModal(direction) {
        document.addEventListener("keydown", this.escDetect.bind(this));
        document.body.classList.add("overflow-hidden");
        document.body.classList.add("relative");

        this.closeBtn.addEventListener("click", () => {
          this.closeModal();
        });

        this.overlay.addEventListener("click", () => {
          this.closeModal();
        });

        this.modal.classList.remove("hidden");
        this.overlay.classList.remove("hidden");
        this.classList.remove("hidden");
        this.modal.style.transform = `translateY(${this.getTranslationY(
          this.modal,
        )}px)`;

        setTimeout(() => {
          this.modal.classList.add(`transition-transform`);
          this.modal.classList.add("ease-in-out");
        }, 1);

        setTimeout(() => {
          this.overlay.classList.add("opacity-50");
          this.overlay.classList.remove("opacity-0");
          this.modal.style.transform = `translateY(0)`;
        }, 2);
      }

      getTranslationY(el) {
        return (window.innerHeight + el.offsetHeight) / 2 + 10;
      }

      closeModal() {
        document.removeEventListener("keydown", this.escDetect.bind(this));
        document.body.classList.remove("overflow-hidden");
        document.body.classList.remove("relative");
        this.modal.style.transform = `translateY(${this.getTranslationY(
          this.modal,
        )}px)`;
        this.overlay.classList.add("opacity-0");
        this.overlay.classList.remove("opacity-50");
        setTimeout(() => {
          this.modal.classList.add("hidden");
          this.overlay.classList.add("hidden");
          this.classList.add("hidden");
        }, 300);
      }

      escDetect(event) {
        if (event.key === "Escape") {
          this.closeModal();
        }
      }
    },
  );
}

if (!customElements.get("geolocation-modal")) {
  customElements.define(
    "geolocation-modal",
    class GeolocationModal extends customElements.get("custom-modal") {
      constructor() {
        super();
        this.detectedCountry = null;
        this.currentCountry = this.dataset.currentCountry;
      }

      connectedCallback() {
        super.connectedCallback();
        this.init();
      }

      init = async () => {
        try {
          const lastShown = localStorage.getItem("geolocationModalLastShown");
          const oneDay = 24 * 60 * 60 * 1000;
          const now = new Date();

          if (lastShown && now - new Date(lastShown) < oneDay) {
            return;
          }

          this.detectedCountry = await this.getLocalizationSuggestion();

          const isCountrySame =
            this.detectedCountry.handle.toLowerCase() ===
            this.currentCountry.toLowerCase();
          if (isCountrySame) return;

          this.openModal();
          localStorage.setItem("geolocationModalLastShown", now.toISOString());
        } catch (error) {
          console.error("Failed to initialize geolocation modal:", error);
        }
      };

      getLocalizationSuggestion = async () => {
        try {
          const response = await fetch(
            `${window.Shopify.routes.root}browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${window.Shopify.country}&language[enabled]=true&language[exclude]=${window.Shopify.language}`,
          );
          if (!response.ok) {
            throw new Error(
              `Network response was not ok: ${response.statusText}`,
            );
          }
          const { detected_values } = await response.json();
          return detected_values.country;
        } catch (error) {
          console.error("Failed to fetch localization suggestion:", error);
          throw error;
        }
      };
    },
  );
}

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector("details");
    this.summaryToggle = this.querySelector("summary");

    this.detailsContainer.addEventListener(
      "keyup",
      (event) => event.code.toUpperCase() === "ESCAPE" && this.close(),
    );

    this.summaryToggle.addEventListener(
      "click",
      this.onSummaryClick.bind(this),
    );

    this.querySelector('button[name="close"]')?.addEventListener(
      "click",
      this.close.bind(this),
    );

    this.summaryToggle.setAttribute("role", "button");
  }

  isOpen() {
    return this.detailsContainer.hasAttribute("open");
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest("details").hasAttribute("open")
      ? this.close()
      : this.open(event);
  }

  onBodyClick(event) {
    if (
      !this.contains(event.target) ||
      event.target.classList.contains("modal-overlay")
    )
      this.close(false);
  }

  open(event) {
    this.onBodyClickEvent =
      this.onBodyClickEvent || this.onBodyClick.bind(this);

    event.target.closest("details").setAttribute("open", true);

    document.body.addEventListener("click", this.onBodyClickEvent);
    document.body.classList.add("overflow-hidden");

    if (this.detailsContainer.querySelector('input:not([type="hidden"])')) {
      trapFocus(
        this.detailsContainer.querySelector('[tabindex="-1"]'),
        this.detailsContainer.querySelector('input:not([type="hidden"])'),
      );
    } else {
      trapFocus(this.detailsContainer.querySelector('[tabindex="-1"]'));
    }
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute("open");
    document.body.removeEventListener("click", this.onBodyClickEvent);
    document.body.classList.remove("overflow-hidden");
  }
}

customElements.define("details-modal", DetailsModal);

if (!customElements.get("toast-notification")) {
  customElements.define(
    "toast-notification",
    class ToastNotification extends HTMLElement {
      constructor() {
        super();
        this._img = this.querySelector("img");
        this._atc = this.querySelector("._atc");
        this._title = this.querySelector("._title");
        this._successIcon = this.querySelector("._success");
        this._errorIcon = this.querySelector("._error");

        document.addEventListener("cart:add", (e) => {
          this._img.src = e.detail.img;
          let title = e.detail.cart.title || e.detail.cart.items[0].title;
          this._title.innerHTML = title.split("|")[0];
          this.classList.add("!-translate-y-[calc(100%+10rem)]");
          this.style.top = "full";

          setTimeout(() => {
            this.classList.remove("!-translate-y-[calc(100%+10rem)]");
            this.style.top = "";
            setTimeout(() => {
              this._img.src = "";
            }, 1000);
          }, 3000);
        });

        document.addEventListener("toast:error", (e) => {
          const { img, errorMessage } = e.detail;
          const originalToastState = this.cloneNode(true);
          this._img.src = img || "";
          this._img.classList.toggle("hidden", !img);
          this._atc.innerHTML = "ERROR";
          this._atc.classList.add("text-destructive");
          this._title.innerHTML = errorMessage || "There is an Error!";
          this._title.classList.add("text-destructive");
          this._errorIcon.classList.remove("hidden");
          this._successIcon.classList.add("hidden");
          this.classList.add(
            "bg-destructive-foreground",
            "!-translate-y-[calc(100%+10rem)]",
            "z-[1000]",
          );
          this.style.top = "full";

          setTimeout(() => {
            this.classList.remove("!-translate-y-[calc(100%+10rem)]");
            this.style.top = "";
            setTimeout(() => {
              this.parentNode.replaceChild(originalToastState, this);
            }, 1000);
          }, 3000);
        });
      }
    },
  );
}

if (!customElements.get("pause-announcement")) {
  customElements.define(
    "pause-announcement",
    class PauseAnnouncement extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.addEventListener("click", () => {
          this.closest("._announcement-bar").classList.toggle("paused");
        });
      }
    },
  );
}

if (!customElements.get("mimic-text")) {
  customElements.define(
    "mimic-text",
    class MimicText extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        if (this.targetObserver) {
          this.targetObserver.disconnect();
        }
      }

      init() {
        this.targetElement = document.querySelector(
          this.getAttribute("data-mimic-target"),
        );
        if (!this.targetElement) return;
        this.updateText();
        this.observeTargetChanges();
      }

      observeTargetChanges() {
        this.targetObserver = new MutationObserver(() => this.updateText());
        this.targetObserver.observe(this.targetElement, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      }

      updateText() {
        const visibleEl = [...this.targetElement.children].find((element) =>
          element.checkVisibility(),
        );
        if (visibleEl) {
          this.textContent = visibleEl.textContent;
        }
      }
    },
  );
}

window.addEventListener("load", () => {
  // select all script and link elements that src(if script) href(if link) includes fastsimon
  const scripts = Array.from(
    document.querySelectorAll('script[src*="fastsimon"]'),
  );
  const links = Array.from(
    document.querySelectorAll('link[href*="fastsimon"]'),
  );
  // remove them from the DOM
  links.forEach((link) => {
    link.remove();
  });
  scripts.forEach((script) => {
    script.remove();
  });
});

function formatMoney(cents, format) {
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }
  var value = "";
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || this.money_format;

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  var match = formatString.match(placeholderRegex);

  if (match) {
    switch (match[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
    }
  }

  return formatString.replace(placeholderRegex, value);
}
